import React, { useEffect, useState } from 'react';
import { ModalBody } from '@chakra-ui/react';
import { DDL } from 'types/general.types';
import { ChipInput } from 'app/components/Core';
import { useFormContext } from 'react-hook-form';
import { getSongUsualSuspects } from 'app/store/SongList/selectors';
import { useAppSelector } from 'app/store';
import { debounce } from 'lodash';
import {
  SuggestedCollaborator,
  SuggestedCollaboratorsList,
} from '../SuggestedCollaboratorsList';
import { EmptySuggestionList } from './EmptySuggestionList';
import { AddCollaboratorsFormData, CollaboratorChip } from '../types';
import { useChips } from './useChips';
import { useCollaboratorSearch } from './useCollaboratorSearch';
import { isValidEmail } from '../utils';

export interface AddCollaboratorDialogComponentProps {
  profilesSearchUrl: string;
  countriesDDL: DDL;
  collaborators: CollaboratorChip[];
}

export const SelectCollaborators: React.FC<AddCollaboratorDialogComponentProps> = ({
  profilesSearchUrl,
  countriesDDL,
  collaborators = [],
}) => {
  const { setValue } = useFormContext<AddCollaboratorsFormData>();

  const usualSuspectlist = useAppSelector(getSongUsualSuspects);
  const [parsedUsualSuspectlist, setParsedUsualSuspectlist] = useState<
    SuggestedCollaborator[]
  >([]);

  const [inviteeEmail, setInviteeEmail] = useState('');
  const {
    chips,
    addChip,
    toggleCollaborator,
    removeAllChips,
    removeChip,
  } = useChips({ collaborators });

  const {
    loaderRef,
    searchResults,
    isSearching,
    isLoading,
    formatChip,
    toggle,
    unselectChip,
    unselectAllCollaborators,
    searchForCollaborators,
    profilesToSuggestedCollaborators,
  } = useCollaboratorSearch({
    chips,
    inputValue: inviteeEmail,
    profilesSearchUrl,
  });

  useEffect(() => {
    setParsedUsualSuspectlist(
      profilesToSuggestedCollaborators(usualSuspectlist)
    );
  }, [profilesToSuggestedCollaborators, usualSuspectlist]);

  useEffect(() => {
    setValue('collaborators', chips);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chips]);

  const handleRemoveChip = (index: number) => {
    const chip = removeChip(index);
    unselectChip(chip);
  };

  const handleRemoveAllChip = () => {
    removeAllChips();
    unselectAllCollaborators();
  };

  const handleToggle = (collaborator: SuggestedCollaborator) => {
    toggleCollaborator(collaborator);
    toggle(collaborator);
  };

  const handleSearchChange = debounce((value) => {
    searchForCollaborators(value);
    setInviteeEmail(isValidEmail(value) ? value : '');
  }, 300);

  return (
    <ModalBody maxHeight="350px" height="100vh" overflow="auto">
      <ChipInput
        placeholder="Search or add by email"
        chips={chips}
        onSubmit={addChip}
        remove={handleRemoveChip}
        removeAll={handleRemoveAllChip}
        onChangeProp={handleSearchChange}
        formatChip={formatChip}
      />
      {!isSearching && usualSuspectlist.length === 0 ? (
        <EmptySuggestionList />
      ) : (
        <SuggestedCollaboratorsList
          countriesDDL={countriesDDL}
          loading={isLoading}
          label={
            !isSearching ? 'Suggestions' : inviteeEmail ? 'Results' : 'Profiles'
          }
          suggestionlist={isSearching ? searchResults : parsedUsualSuspectlist}
          loaderRef={loaderRef}
          onToggle={handleToggle}
          sx={{ mt: 4 }}
        />
      )}
    </ModalBody>
  );
};
